import { Option } from '@joor/design-system'

import { Location } from '__generated__/atlas-types'

import { formatRetailerWithLocation } from 'formatters/orders'
import { formatNameWithCodeAndDash } from 'utils/formatters'
import { formatDropdownOptions } from 'utils/formatters'
import { fromGlobalId } from 'utils/transformations/graphql'

import {
  BillingAddress,
  Customer,
  Linesheet,
  OrderStatus,
  PaymentMethod,
  PriceType,
  ShippingAddress,
  ShippingMethod,
} from './SecondStepVerticalHorizontal.types'

export const MINIMUM_CHARACTERS_QUERY = 3

const formatOrderStatusLabel = (value: string): string => {
  const str = value.toLowerCase().replaceAll('_', ' ').split(' ')

  for (let i = 0; i < str.length; i++) {
    str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1)
  }

  return str.join(' ')
}

export const getOrderStatusOptions = (isLiteRetailer: boolean) => [
  isLiteRetailer
    ? {
        label: formatOrderStatusLabel(OrderStatus.IN_PROGRESS),
        value: OrderStatus.IN_PROGRESS,
      }
    : {
        label: formatOrderStatusLabel(OrderStatus.NOTES),
        value: OrderStatus.NOTES,
      },
]

export const getCustomerOptions = (customers: Customer[]): Option[] =>
  formatDropdownOptions(
    customers,
    formatRetailerWithLocation,
    true,
    'notranslate',
    'id',
  )

export const getLineSheetOptions = (lineSheets: Linesheet[]): Option[] =>
  lineSheets.map((lineSheet) => ({
    label: lineSheet?.name,
    value: lineSheet?.id,
  }))

export const getPriceTypesOptions = (priceTypes: PriceType[]): Option[] =>
  priceTypes.map((priceType) => ({
    label: formatNameWithCodeAndDash(
      priceType?.name,
      priceType?.currency?.code,
    ),
    value: priceType?.id,
  }))

export const getBillingAddressOptions = (
  billingAddresses: BillingAddress[],
): Option[] =>
  billingAddresses.map((billingAddress) => ({
    label: formatNameWithCodeAndDash(
      billingAddress?.address?.companyName,
      billingAddress?.address?.city,
    ),
    value: billingAddress?.id,
  }))

export const getShippingAddressesOptions = (
  shippingAddresses: ShippingAddress[],
): Option[] =>
  shippingAddresses.map((shippingAddress) => ({
    label: formatNameWithCodeAndDash(
      shippingAddress?.address?.companyName,
      shippingAddress?.address?.city,
    ),
    value: shippingAddress?.id,
  }))

export const getPaymentMethodsOptions = (
  paymentMethods: PaymentMethod[],
): Option[] =>
  paymentMethods.map((paymentMethod) => ({
    label:
      formatNameWithCodeAndDash(
        paymentMethod?.paymentMethod?.name,
        paymentMethod?.paymentMethod?.code,
      ) || '',
    value: paymentMethod?.paymentMethod?.id || '',
  }))

export const getShippingMethodsOptions = (
  shippingMethods: ShippingMethod[],
): Option[] =>
  shippingMethods.reduce((options, { shippingMethod }) => {
    if (shippingMethod) {
      options.push({
        label: formatNameWithCodeAndDash(
          shippingMethod.name,
          shippingMethod.code,
        ),
        value: shippingMethod.id,
      })
    }
    return options
  }, [] as Option[])

const formatRetailerLocation = (location: Location) => {
  const { address1, address2, city, country, state } = location
  const address = [address1, address2].filter(Boolean).join(' ')
  return [address, city, state, country].filter(Boolean).join(', ')
}

export const getRetailerPrimaryLocationOption = (
  location: Location | undefined,
): Option | undefined => {
  if (!location) {
    return
  }
  const locationID = fromGlobalId(location.id).id
  if (!locationID && !location.name && !location.city) {
    return
  }
  return {
    value: location.id,
    label: Boolean(location.name)
      ? `${location.name} - ${location.city}`
      : formatRetailerLocation(location),
  } as Option
}
